/**
 * Autopilot banner (spec §7): the platform's recorded verdict for this
 * incident, one sentence of reasoning, the agreement once the incident is
 * closed, and the adjudication buttons. L2/admin only — the API answers 403
 * for anyone else and the banner renders nothing. It states an opinion the
 * platform recorded for comparison; nothing here acts on the incident.
 */
import { useState } from 'react';
import { Bot, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import { adjudicateAutopilot, DISPOSITION_LABEL, ADJUDICATION_LABEL, DISPOSITION_STYLE, AGREEMENT_STYLE, AGREEMENT_LABEL, EXECUTION_LABEL, EXECUTION_STYLE, type IncidentAutopilot, type AdjudicationVerdict, type Disposition, type Confidence } from '../../services/autopilot';

export function Chip({ className, children, title }: { className?: string; children: React.ReactNode; title?: string }) {
  return <span title={title} className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border', className)}>{children}</span>;
}

export function DispositionChip({ disposition, confidence }: { disposition: Disposition; confidence?: Confidence }) {
  return (
    <Chip className={DISPOSITION_STYLE[disposition]}>
      <Bot className="w-3.5 h-3.5" />
      {DISPOSITION_LABEL[disposition]}
      {confidence && <span className="opacity-70">· {confidence} confidence</span>}
    </Chip>
  );
}

interface Props {
  tenantId: string;
  incidentId: string;
  data: IncidentAutopilot;
  onOpenCaseFile: () => void;
  onAdjudicated: (data: IncidentAutopilot) => void;
}

const QUICK_VERDICTS: AdjudicationVerdict[] = ['agree', 'should-have-escalated', 'should-have-closed'];

export function AutopilotBanner({ tenantId, incidentId, data, onOpenCaseFile, onAdjudicated }: Props) {
  const [saving, setSaving] = useState<AdjudicationVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run, agreement, human } = data;
  const decision = run.record?.verdict.reasoning.decision ?? '';
  const arrivedAt = human.arrivedAt ?? run.record?.intake.incident.createdDateTime ?? null;
  const workMs = new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime();
  const sinceArrivalMs = arrivedAt ? new Date(run.completedAt).getTime() - new Date(arrivedAt).getTime() : null;
  const fmt = (ms: number) => (ms < 60_000 ? `${Math.round(ms / 1000)} s` : ms < 3_600_000 ? `${Math.round(ms / 60_000)} min` : `${(ms / 3_600_000).toFixed(1)} h`);
  const cycle = `verdict after ${fmt(workMs)} of work${sinceArrivalMs !== null && sinceArrivalMs >= 0 ? `, ${fmt(sinceArrivalMs)} after arrival` : ''}`;

  const adjudicate = async (verdict: AdjudicationVerdict) => {
    setSaving(verdict); setError(null);
    const result = await adjudicateAutopilot(tenantId, incidentId, verdict, '');
    setSaving(null);
    if (!result.success) { setError(result.error ?? 'Could not record the adjudication'); return; }
    onAdjudicated({ ...data, run: { ...run, adjudication: result.data.adjudication } });
  };

  return (
    <div className="rounded-xl border border-border-default bg-bg-secondary p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-text-secondary font-medium mr-1">Autopilot</span>
        <DispositionChip disposition={run.disposition} confidence={run.confidence} />
        {run.modelDisposition !== run.disposition && (
          <Chip className="bg-bg-tertiary text-text-secondary border-border-default" title="The model proposed a different disposition from the code rule">model: {DISPOSITION_LABEL[run.modelDisposition]}</Chip>
        )}
        {agreement !== 'open' && <Chip className={AGREEMENT_STYLE[agreement]}>{AGREEMENT_LABEL[agreement]}</Chip>}
        <Chip className="bg-bg-tertiary text-text-secondary border-border-default" title={`Started ${new Date(run.startedAt).toLocaleTimeString('en-AU')}, verdict ${new Date(run.completedAt).toLocaleTimeString('en-AU')} (${run.rounds} round(s))`}>{cycle}</Chip>
        {run.execution && <Chip className={EXECUTION_STYLE[run.execution.decision]} title={run.execution.reason ?? undefined}>{EXECUTION_LABEL[run.execution.decision]}</Chip>}
        {run.adjudication && <Chip className="bg-accent-blue/15 text-accent-blue border-accent-blue/40">Adjudicated: {ADJUDICATION_LABEL[run.adjudication.verdict]}</Chip>}
        <button onClick={onOpenCaseFile} className="ml-auto inline-flex items-center gap-1 text-sm text-accent-blue hover:underline">
          Open case file <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      {decision && <p className="text-sm text-text-primary leading-relaxed">{decision}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {data.canAdjudicate === false ? (
          <span className="text-xs text-text-secondary">
            {run.adjudication ? `Adjudicated ${ADJUDICATION_LABEL[run.adjudication.verdict].toLowerCase()} by ${run.adjudication.by ?? 'the adjudicator'}.` : 'Not yet adjudicated.'} Adjudication is recorded by the closure approver.
          </span>
        ) : (<>
        <span className="text-xs text-text-secondary">Your adjudication:</span>
        {QUICK_VERDICTS.map((v) => (
          <Button key={v} size="sm" variant={run.adjudication?.verdict === v ? 'primary' : 'secondary'} disabled={saving !== null} onClick={() => adjudicate(v)}>
            {saving === v ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {ADJUDICATION_LABEL[v]}
          </Button>
        ))}
        </>)}
        <span className="text-xs text-text-secondary">{run.execution?.decision === 'executed' ? 'Closed by Alexein Autopilot under this customer\'s policy; the customer sees an ordinary closure.' : 'Not done by the platform — its opinion, recorded for comparison.'}</span>
        {error && <span className="text-xs text-accent-red">{error}</span>}
      </div>
    </div>
  );
}
